import { z } from 'zod';

/**
 * Typed key-value settings (§12 limits and the 9 sections of the mockup's settings screen —
 * decision 3, TZ-уточнення 2026-08-26; see `SETTING_SECTIONS` below for why 9, not 11).
 * The key set is closed: adding a knob means adding it here, which forces a schema and a
 * default rather than a magic string read at three call sites.
 */

export const settingsSchema = z.object({
  // ── AI та моделі ────────────────────────────────────────────────────────────────
  'ai.default_provider': z.enum(['openrouter', 'openai', 'anthropic']).default('openrouter'),
  'ai.default_model': z.string().default('anthropic/claude-sonnet-5'),
  'ai.temperature': z.number().min(0).max(2).default(0.7),
  'ai.max_parallel_generations': z.number().int().min(1).max(64).default(6),
  'ai.daily_token_limit_per_key': z.number().int().min(0).default(5_000_000),
  /**
   * Which key the daily budget is counted against, per provider.
   *
   * Empty falls back to the provider name, which is what the counter used unconditionally
   * before — two keys on one provider shared a budget, and the number on the settings screen
   * belonged to a key nobody could name.
   */
  'ai.openrouter_key_id': z.string().default(''),
  'ai.openai_key_id': z.string().default(''),
  'ai.anthropic_key_id': z.string().default(''),
  'ai.max_retries': z.number().int().min(0).max(10).default(5),

  // ── Контент і мова ──────────────────────────────────────────────────────────────
  'content.tone_of_voice': z.string().default('нейтрально-експертний'),
  'content.min_words_default': z.number().int().min(0).default(400),
  'content.max_words_default': z.number().int().min(0).default(1200),
  'content.forbidden_phrases': z.array(z.string()).default([]),
  'content.uniqueness_threshold_pct': z.number().min(0).max(100).default(85),
  'content.dictionary_coverage_min_pct': z.number().min(0).max(100).default(90),

  // ── SEO-дефолти ─────────────────────────────────────────────────────────────────
  'seo.title_template': z.string().default('{main_kw} — {brand}'),
  'seo.description_template': z.string().default('{brand}: {bonus_list}. {geo} · {lang}'),
  'seo.enable_faq_schema': z.boolean().default(true),
  'seo.enable_breadcrumb_schema': z.boolean().default(true),
  'seo.max_html_kb': z.number().int().min(1).default(150),

  // ── Тех і деплой ────────────────────────────────────────────────────────────────
  'tech.default_canonical': z.enum(['self', 'drop', 'site']).default('self'),
  'tech.default_hreflang_role': z.enum(['lang', 'x-default', 'none']).default('lang'),
  'tech.entity_fill_threshold_pct': z.number().min(0).max(100).default(70),

  // ── Лінкбілдинг ─────────────────────────────────────────────────────────────────
  'links.default_anchor_pct': z.number().min(0).max(100).default(40),
  'links.min_donor_dr': z.number().int().min(0).max(100).default(20),
  'links.max_spam_signals_pct': z.number().min(0).max(100).default(30),
  'links.guest_post_min_words': z.number().int().min(0).default(600),

  // ── Ліміти й запобіжники ────────────────────────────────────────────────────────
  'limits.bulk_max_domains_per_window': z.number().int().min(1).default(50),
  'limits.bulk_window_minutes': z.number().int().min(1).default(60),
  'limits.bulk_batch_size': z.number().int().min(1).default(10),
  'limits.traffic_at_risk_threshold_clicks': z.number().int().min(0).default(1000),
  'limits.registrar_rate_per_minute': z.number().int().min(1).default(20),
  'limits.gsc_rate_per_minute': z.number().int().min(1).default(60),
  'limits.confirmation_ttl_minutes': z.number().int().min(1).default(120),
  /**
   * Decision 4 (TZ-уточнення, 2026-08-26): двоособове підтвердження купівлі EMD від грошового
   * порога, одне підтвердження нижче. $1000 (мінорні одиниці USD — той самий формат, що й
   * `emd_candidates.price`) — дефолт, обраний на імплементації: реєстраторська купівля вільного
   * домену коштує одиниці-десятки доларів (одне підтвердження проходить без гальм), а
   * aftermarket-лоти в тестових фікстурах лежать у діапазоні $500–5500 (саме туди і має влучати
   * гейт). Потребує підпису капітана — див. DECISIONS.md.
   */
  'limits.emd_purchase_dual_confirm_threshold_minor': z.number().int().min(0).default(100_000),

  // ── Бренд-дефолти ───────────────────────────────────────────────────────────────
  'brand.default_currency': z.string().length(3).default('EUR'),
  'brand.rounding_rule': z.enum(['none', 'nice_10', 'nice_50', 'nice_100']).default('nice_10'),

  // ── Моніторинг ──────────────────────────────────────────────────────────────────
  'monitor.ping_interval_minutes': z.number().int().min(1).default(5),
  'monitor.index_drop_incident_pct': z.number().min(0).max(100).default(30),
  'monitor.uptime_timeout_ms': z.number().int().min(100).default(10_000),

  // ── Сповіщення ──────────────────────────────────────────────────────────────────
  'notify.on_incident': z.boolean().default(true),
  'notify.on_abuse_critical': z.boolean().default(true),
  'notify.on_dlq_digest': z.boolean().default(true),
  'notify.on_deploy': z.boolean().default(false),

  // ── Імпорт ──────────────────────────────────────────────────────────────────────
  'import.max_file_mb': z.number().int().min(1).max(200).default(50),
  'import.batch_size': z.number().int().min(1).max(10_000).default(1000),
  /**
   * Mockup, «Ядро та структура»: «Мін. обʼєм кейворда 30 / міс — нижче не імпортується».
   * A keyword nobody searches for costs a generated page and returns nothing.
   */
  'import.keyword_min_volume': z.number().int().min(0).default(30),
});

export type Settings = z.infer<typeof settingsSchema>;
export type SettingKey = keyof Settings;

export const SETTING_KEYS = Object.keys(settingsSchema.shape) as SettingKey[];

export function defaultSettings(): Settings {
  return settingsSchema.parse({});
}

export function settingSchemaFor(key: SettingKey): z.ZodType {
  return settingsSchema.shape[key];
}

/**
 * Sections of the settings screen. The mockup renders nine KV groups plus «Інтеграції та
 * ключі», which is not key-value at all — it lists rows of the `integrations` table. It is
 * declared here so navigation and the RBAC gate treat it like the rest of the screen.
 */
export const SETTING_SECTIONS: {
  id: string;
  label: string;
  note: string;
  prefix: string | null;
  /**
   * Додаткові префікси, які лягають у цю ж вкладку.
   *
   * Мокап тримає рівно девʼять вкладок (`TABS`, рядок 4270), а в нас їх було одинадцять:
   * зайвими були «Моніторинг» і «Імпорт». Просте видалення дескрипторів лишило б шість
   * ключів — інтервал пінга, поріг просадки індексу, таймаут, ліміти файлів і батчів — без
   * жодного екрана: система їх читає, а задати їх нема де. Тому вкладки прибрано, а ключі
   * переїхали в ті, куди їх кладе сам мокап: моніторинг — до «Тех і деплой», ліміти
   * імпорту — до «Контент і мова».
   */
  alsoPrefixes?: string[];
}[] = [
  { id: 'ai', label: 'AI та моделі', note: 'дефолти для будь-якої генерації', prefix: 'ai.' },
  {
    id: 'content',
    label: 'Контент і мова',
    note: 'правила, за якими пишеться будь-який текст',
    prefix: 'content.',
    alsoPrefixes: ['import.'],
  },
  {
    id: 'seo',
    label: 'SEO-дефолти',
    note: 'звідки беруться title, schema та структура',
    prefix: 'seo.',
  },
  {
    id: 'tech',
    label: 'Тех і деплой',
    note: 'як сайти виливаються та клеяться',
    prefix: 'tech.',
    alsoPrefixes: ['monitor.'],
  },
  {
    id: 'links',
    label: 'Лінкбілдинг',
    note: 'дефолти для планів і гестпостів',
    prefix: 'links.',
  },
  {
    id: 'integrations',
    label: 'Інтеграції та ключі',
    note: 'усе, що тягне дані ззовні',
    prefix: null,
  },
  {
    id: 'limits',
    label: 'Ліміти й запобіжники',
    note: 'що система не дасть зробити випадково',
    prefix: 'limits.',
  },
  {
    id: 'brand',
    label: 'Бренд-дефолти',
    note: 'що підставляється, коли в бренді не заповнено',
    prefix: 'brand.',
  },
  { id: 'notify', label: 'Сповіщення', note: 'кого і коли будити', prefix: 'notify.' },
];
